import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import { areas, nearbyRoads } from "@/mocks/area";
import { Link } from "react-router-dom";
import TiltCard from "@/components/base/TiltCard";

const propertyTypes = [
  { icon: "ri-home-2-line", label: "Terraced houses" },
  { icon: "ri-building-line", label: "Flats & maisonettes" },
  { icon: "ri-store-2-line", label: "Independent shops" },
  { icon: "ri-cup-line", label: "Small cafés" },
  { icon: "ri-briefcase-4-line", label: "Small offices" },
  { icon: "ri-key-2-line", label: "Rental properties" },
];

export default function AreaPage() {
  return (
    <>
      <PageHero
        eyebrow="Service area"
        title={
          <>
            Islington &amp; nearby<br />
            <em className="not-italic text-accent-400">North London.</em>
          </>
        }
        intro="Based in Islington and serving nearby North London homes, flats, landlords and small businesses."
        image="https://readdy.ai/api/search-image?query=Peaceful%20Islington%20London%20residential%20street%20with%20a%20row%20of%20Victorian%20terraced%20brick%20houses%2C%20wrought%20iron%20railings%2C%20black%20front%20doors%20and%20soft%20green%20trees%2C%20warm%20golden%20late%20afternoon%20light%2C%20realistic%20editorial%20photography&width=1400&height=900&seq=area-hero-01&orientation=landscape"
        imageAlt="Islington residential terraced street"
        primaryLabel="Check my postcode"
        secondaryLabel="See services"
        secondaryTo="/services"
      />

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-background-100 border border-background-200 text-[11px] uppercase tracking-[0.18em] text-foreground-700">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              Where I work
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05]">
              Just one local zone,<br />
              <em className="not-italic text-accent-600">done properly.</em>
            </h2>
            <p className="mt-4 text-foreground-600 max-w-md">
              I work in one local area so I can get to jobs quickly and know
              the neighbourhood. If you're outside the zone, drop me a message
              and I'll point you to a good local electrician.
            </p>

            <div className="mt-8 rounded-2xl overflow-hidden border border-background-200">
              <iframe
                title="Islington service area map"
                src="https://www.google.com/maps?q=Islington%2C%20London&t=&z=13&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="360"
                loading="lazy" decoding="async"
                style={{ border: 0 }}
              ></iframe>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {areas.map((a) => (
                <TiltCard key={a.name} className="rounded-2xl" rotation={7} scale={1.02}>
                <div
                  className={`group relative rounded-2xl overflow-hidden ${a.tint} border border-background-200 transition-colors duration-300 h-full`}
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={a.image}
                      alt={`${a.name} neighbourhood`}
                      loading="lazy" decoding="async"
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5 bg-background-50">
                    <div className="flex items-center justify-between">
                      <p className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                        {a.name}
                      </p>
                      <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-600">
                        {a.postcodes}
                      </span>
                    </div>
                    <p className="mt-1 text-xs md:text-sm text-foreground-600">
                      {a.note}
                    </p>
                  </div>
                </div>
                </TiltCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-background-50 border border-background-200 text-[11px] uppercase tracking-[0.18em] text-foreground-700">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              Nearby roads
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05]">
              Streets I know<br />
              <em className="not-italic text-accent-600">very well.</em>
            </h2>
            <p className="mt-4 text-foreground-600 max-w-md">
              Not sure if you're in the zone? Send me your postcode — most of
              the roads below and everything nearby are covered.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {nearbyRoads.map((r) => (
                <span
                  key={r}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-800"
                >
                  <i className="ri-map-pin-2-line text-accent-600"></i>
                  {r}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-background-50 border border-background-200 text-[11px] uppercase tracking-[0.18em] text-foreground-700">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              What I cover
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05]">
              Homes, flats,<br />
              <em className="not-italic text-accent-600">and small businesses.</em>
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {propertyTypes.map((p) => (
                <div
                  key={p.label}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-accent-500 transition-colors"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-accent-700">
                    <i className={`${p.icon} text-lg`}></i>
                  </span>
                  <p className="text-sm font-semibold text-foreground-950">{p.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/book"
                className="group inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-primary-950 text-background-50 text-sm font-semibold hover:bg-primary-900 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
              >
                Book a local visit
                <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Local only"
        title={
          <>
            Not sure if I cover<br />
            <em className="not-italic text-accent-600">your postcode?</em>
          </>
        }
        subtitle="Send it over and I'll get back within a few hours."
        primaryLabel="Check availability"
        secondaryLabel="Send a message"
        secondaryTo="/contact"
      />
    </>
  );
}